import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FaCalendarAlt, FaCog, FaMagic, FaCheck, FaExclamationTriangle,
  FaUserTie, FaSync, FaChevronLeft, FaChevronRight, FaEdit,
  FaSave, FaTimes, FaCheckCircle, FaClipboardList, FaBan, FaPlus,
  FaGripVertical, FaExchangeAlt, FaInfoCircle,
} from "react-icons/fa";
import {useTheme, getT, getBrandColors, lighten} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import API_BASE from "./apiConfig";
import { useSort } from "./hooks/useSort";

const BASE = API_BASE;
const BLUR = "blur(18px)";
const FONT = "'Segoe UI', system-ui, -apple-system, sans-serif";
const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const ALL_DAYS = [
  { dow: 0, label: "Mon" }, { dow: 1, label: "Tue" }, { dow: 2, label: "Wed" },
  { dow: 3, label: "Thu" }, { dow: 4, label: "Fri" }, { dow: 5, label: "Sat" },
];

const COACH_PALETTE = [
  { fg: "#6366f1", bg: "rgba(99,102,241,0.15)" },
  { fg: "#0ea5e9", bg: "rgba(14,165,233,0.15)" },
  { fg: "#10b981", bg: "rgba(16,185,129,0.15)" },
  { fg: "#f59e0b", bg: "rgba(245,158,11,0.15)" },
  { fg: "#ec4899", bg: "rgba(236,72,153,0.15)" },
  { fg: "#8b5cf6", bg: "rgba(139,92,246,0.15)" },
  { fg: "#ef4444", bg: "rgba(239,68,68,0.15)" },
  { fg: "#14b8a6", bg: "rgba(20,184,166,0.15)" },
];
const coachColor = (id) => COACH_PALETTE[(id || 0) % COACH_PALETTE.length];

function makeS(T) {
  return {
    card: { background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR, border: T.glassBdr, borderRadius: 18, boxShadow: T.glassSh, padding: "24px 28px", boxSizing: "border-box" },
    lbl:  { display: "block", marginBottom: 6, fontSize: 13, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: T.textLabel, fontFamily: FONT },
    inp:  { width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText, fontSize: 15, fontFamily: FONT, boxSizing: "border-box", outline: "none" },
    yBtn: { padding: "11px 22px", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", color: getBrandColors().blueDark, background: `linear-gradient(135deg,${T.yellow},#fff3a0)`, boxShadow: "0 4px 14px rgba(255,200,0,0.28)", display: "flex", alignItems: "center", gap: 8, fontFamily: FONT },
    bBtn: { padding: "11px 22px", border: `1px solid ${T.inputBdr}`, borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", color: T.inputText, background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR, display: "flex", alignItems: "center", gap: 8, fontFamily: FONT },
    gBtn: { padding: "9px 16px", border: `1.5px solid ${T.inputBdr}`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", color: T.textLabel, background: T.glass, display: "flex", alignItems: "center", gap: 6, fontFamily: FONT },
    sBtn: { padding: "11px 24px", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", color: "#fff", background: `linear-gradient(135deg,${lighten(getBrandColors().green,20)},${getBrandColors().green})`, boxShadow: "0 4px 14px rgba(16,185,129,0.35)", display: "flex", alignItems: "center", gap: 8, fontFamily: FONT },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function slotHrs(cfg, slotType, isSat) {
  if (!cfg) return 1;
  const prefix = isSat ? "saturday" : "weekday";
  const [sh, sm] = (cfg[`${prefix}_${slotType}_start`] || "16:00").split(":").map(Number);
  const [eh, em] = (cfg[`${prefix}_${slotType}_end`]   || "20:00").split(":").map(Number);
  return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
}

function computeCoachHours(assignments, cfg) {
  const map = {};
  for (const a of assignments) {
    if (a.status === "cancelled") continue;
    const isSat = new Date(a.assign_date + "T00:00:00").getDay() === 6;
    const h = slotHrs(cfg, a.slot_type || "coaching", isSat);
    map[a.coach_id] = (map[a.coach_id] || 0) + h;
  }
  return map;
}

// ─── Hours Bar ────────────────────────────────────────────────────────────────
function HoursBar({ used, max, compact = false }) {
  const pct = max > 0 ? Math.min(100, (used / max) * 100) : 0;
  const color = pct >= 100 ? "#ef4444" : pct >= 80 ? "#f59e0b" : "#10b981";
  const label = pct >= 100 ? "Over" : pct >= 80 ? "High" : "OK";
  return (
    <div>
      {!compact && (
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, color, fontFamily: FONT, marginBottom: 4 }}>
          <span>{used.toFixed(1)}h / {max}h</span><span>{label}</span>
        </div>
      )}
      <div style={{ height: compact ? 5 : 8, borderRadius: 4, background: "rgba(128,128,128,0.15)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 3, transition: "width 0.3s" }} />
      </div>
    </div>
  );
}

// ─── KPI Bar ──────────────────────────────────────────────────────────────────
function KpiBar({ assignments, T }) {
  const total        = assignments.length;
  const uniqueCoaches = new Set(assignments.map(a => a.coach_id)).size;
  const uniqueBatches = new Set(assignments.map(a => a.batch_id)).size;
  const approved     = assignments.filter(a => a.status === "approved").length;
  const overLimit    = assignments.filter(a => a.over_limit).length;
  const kpis = [
    { label: "Total Assignments", value: total,         color: T.yellow },
    { label: "Coaches Assigned",  value: uniqueCoaches, color: "#6366f1" },
    { label: "Batches Covered",   value: uniqueBatches, color: "#0ea5e9" },
    { label: "Approved",          value: approved,      color: "#10b981" },
    { label: "Over Limit",        value: overLimit,     color: overLimit ? "#ef4444" : "#10b981" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
      {kpis.map(k => (
        <div key={k.label} style={{ background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR, border: T.glassBdr, borderRadius: 14, padding: "18px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: k.color, fontFamily: FONT }}>{k.value}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", fontFamily: FONT, marginTop: 4 }}>{k.label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Draggable Coach Card (in roster panel) ───────────────────────────────────
// `draggable`/onDragStart/onDragEnd give desktop mouse users native drag-and-drop.
// That API has no touch equivalent, so onClick/isSelected give phone/tablet users
// a tap-to-select-then-tap-a-slot path to the same assignment (see BatchSlot).
function CoachCard({ coach, hoursUsed, maxHours, isAvailable, isDragging, isSelected, onDragStart, onDragEnd, onClick, T }) {
  const { fg, bg } = coachColor(coach.coach_id);
  const pct = maxHours > 0 ? (hoursUsed / maxHours) * 100 : 0;
  const overPct = pct >= 100;
  const highPct = pct >= 80 && pct < 100;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      style={{
        padding: "12px 14px", borderRadius: 12, marginBottom: 10, cursor: "pointer",
        background: (isDragging || isSelected) ? bg : T.glass,
        border: (isDragging || isSelected) ? `2px solid ${fg}` : T.glassBdr,
        backdropFilter: BLUR, WebkitBackdropFilter: BLUR, opacity: isDragging ? 0.6 : 1,
        transition: "all 0.15s", userSelect: "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <FaGripVertical size={12} style={{ color: T.textMuted, flexShrink: 0 }} />
        <div style={{ width: 34, height: 34, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <FaUserTie size={15} style={{ color: fg }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: FONT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {coach.coach_name}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
            {!isAvailable ? (
              <span style={{ fontSize: 11, fontWeight: 700, color: getBrandColors().red, fontFamily: FONT }}>
                <FaBan size={9} /> Unavailable
              </span>
            ) : overPct ? (
              <span style={{ fontSize: 11, fontWeight: 700, color: "#ef4444", fontFamily: FONT }}>⚠ Over limit</span>
            ) : highPct ? (
              <span style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", fontFamily: FONT }}>↑ High load</span>
            ) : (
              <span style={{ fontSize: 11, fontWeight: 700, color: "#10b981", fontFamily: FONT }}>✓ Available</span>
            )}
          </div>
        </div>
      </div>
      <HoursBar used={hoursUsed} max={maxHours} compact />
      <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 4, textAlign: "right" }}>
        {hoursUsed.toFixed(1)}h / {maxHours}h
      </div>
    </div>
  );
}

// ─── Batch Slot (drop zone in day board) ──────────────────────────────────────
function BatchSlot({ batch, dayAssignments, dragCoach, selectedCoach, onCoachAssigned, month, year, dateStr, cfg, coachHoursMap, maxHoursMap, availMap, updateAssignments, T, S }) {
  const [dragOver, setDragOver] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const myAssigns = dayAssignments.filter(a => a.batch_id === batch.batch_id);
  const needed  = Math.max(1, Math.ceil((batch.enrolled_count || 1) / (parseInt(cfg?.students_per_coach) || 10)));
  const assigned = myAssigns.length;
  const isShort  = assigned < needed;
  const isOver   = assigned > needed;

  // Assignment logic, shared by the mouse-drag drop handler and the
  // tap-to-select-then-tap-a-slot fallback used on touch devices (native HTML5
  // drag-and-drop has no touch equivalent, so this is how phones/tablets assign).
  const assignCoach = async (targetCoach) => {
    const alreadyHere = myAssigns.some(a => a.coach_id === targetCoach.coach_id);
    if (alreadyHere) return;

    const isSat = new Date(dateStr + "T00:00:00").getDay() === 6;
    const hrs   = slotHrs(cfg, "coaching", isSat);
    const used  = coachHoursMap[targetCoach.coach_id] || 0;
    const max   = maxHoursMap[targetCoach.coach_id] || parseFloat(cfg?.max_coach_hours_month) || 40;
    const willOverLimit = (used + hrs) > max;

    // Optimistic local add with temp id
    const tempId = `tmp_${Date.now()}`;
    const optimistic = {
      id: tempId, plan_month: month, plan_year: year,
      coach_id: targetCoach.coach_id, coach_name: targetCoach.coach_name,
      batch_id: batch.batch_id, batch_name: batch.batch_name,
      assign_date: dateStr, slot_type: "coaching",
      status: "draft", over_limit: willOverLimit ? 1 : 0,
    };
    updateAssignments(prev => [...prev, optimistic]);

    // Persist — swap temp id for real id on response. The backend now enforces
    // availability (rejects with 400) and recomputes the real over_limit flag —
    // previously this endpoint accepted any coach/date unconditionally, so a
    // blocked-off coach could be assigned with no pushback and over_limit was
    // always hardcoded to 0 regardless of actual hours.
    try {
      const res = await fetch(`${BASE}/coach-planner/assignments`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ month, year, coach_id: targetCoach.coach_id, batch_id: batch.batch_id, date: dateStr, slot_type: "coaching" }),
      });
      const data = await res.json();
      if (!res.ok) {
        updateAssignments(prev => prev.filter(a => a.id !== tempId));
        setErrorMsg(data.detail || "Could not assign coach.");
        setTimeout(() => setErrorMsg(null), 4000);
        return;
      }
      updateAssignments(prev => prev.map(a => a.id === tempId
        ? { ...a, id: data.id, over_limit: data.over_limit ? 1 : 0 }
        : a));
    } catch {
      // Roll back on network failure
      updateAssignments(prev => prev.filter(a => a.id !== tempId));
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    let droppedCoach;
    try { droppedCoach = JSON.parse(e.dataTransfer.getData("text/plain")); } catch { return; }
    await assignCoach(droppedCoach);
  };

  const handleTapAssign = async () => {
    if (!selectedCoach) return;
    await assignCoach(selectedCoach);
    onCoachAssigned?.();
  };

  const removeAssign = async (id) => {
    // Optimistic remove
    updateAssignments(prev => prev.filter(a => a.id !== id));
    try {
      await fetch(`${BASE}/coach-planner/assignments/${id}`, { method: "DELETE" });
    } catch {
      // On failure we don't roll back — refresh button will reconcile
    }
  };

  // Can the dragged (or tap-selected) coach be accepted?
  const activeCoach = dragCoach || selectedCoach;
  let dropState = "neutral";
  if (activeCoach) {
    const isAvail = availMap ? (availMap[activeCoach.coach_id] !== false) : true;
    const alreadyHere = myAssigns.some(a => a.coach_id === activeCoach.coach_id);
    const isSat = new Date(dateStr + "T00:00:00").getDay() === 6;
    const hrs = slotHrs(cfg, "coaching", isSat);
    const used = coachHoursMap[activeCoach.coach_id] || 0;
    const max  = maxHoursMap[activeCoach.coach_id] || 40;
    if (alreadyHere) dropState = "reject";
    else if (!isAvail) dropState = "unavail";
    else if (used + hrs > max) dropState = "overlimit";
    else dropState = "accept";
  }

  const dropColors = {
    accept:   { bg: "rgba(16,185,129,0.15)", border: "rgba(16,185,129,0.5)" },
    reject:   { bg: "rgba(239,68,68,0.08)",  border: "rgba(239,68,68,0.4)" },
    unavail:  { bg: "rgba(239,68,68,0.08)",  border: "rgba(239,68,68,0.4)" },
    overlimit:{ bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.5)" },
    neutral:  { bg: "transparent",           border: "transparent" },
  };
  const dc = dropColors[(dragOver || selectedCoach) ? dropState : "neutral"];

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={handleTapAssign}
      style={{
        padding: "12px 14px", borderRadius: 12, marginBottom: 10,
        border: `2px dashed ${activeCoach ? dropColors[dropState].border : T.glassBdr}`,
        background: (dragOver || selectedCoach) ? dc.bg : T.glass,
        backdropFilter: BLUR, WebkitBackdropFilter: BLUR, transition: "all 0.15s",
        cursor: selectedCoach ? "pointer" : "default",
      }}
    >
      {/* Batch header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div>
          <span style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: FONT }}>{batch.batch_name}</span>
          {batch.enrolled_count > 0 && (
            <span style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, marginLeft: 10 }}>
              {batch.enrolled_count} students
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12, fontWeight: 700, fontFamily: FONT, padding: "4px 10px", borderRadius: 10,
            background: isShort ? "rgba(239,68,68,0.12)" : isOver ? "rgba(245,158,11,0.12)" : "rgba(16,185,129,0.12)",
            color:      isShort ? getBrandColors().red               : isOver ? getBrandColors().orange               : getBrandColors().green }}>
            {isShort ? `⚠ Need ${needed - assigned} more` : isOver ? `↑ ${assigned - needed} extra` : `✓ Covered`}
          </span>
          <span style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT }}>
            {assigned}/{needed}
          </span>
        </div>
      </div>

      {/* Assigned coaches chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, minHeight: 30 }}>
        {myAssigns.map(a => {
          const { fg, bg } = coachColor(a.coach_id);
          const isSat = new Date(dateStr + "T00:00:00").getDay() === 6;
          const hrs = slotHrs(cfg, a.slot_type || "coaching", isSat);
          const used = coachHoursMap[a.coach_id] || 0;
          const max  = maxHoursMap[a.coach_id] || 40;
          const isOL = a.over_limit || used > max;
          return (
            <span key={a.id} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px", borderRadius: 20, fontSize: 13, fontWeight: 700, fontFamily: FONT,
              background: isOL ? "rgba(239,68,68,0.15)" : bg,
              color:      isOL ? getBrandColors().red : fg,
              border:     isOL ? "1.5px solid rgba(239,68,68,0.35)" : `1.5px solid ${fg}33`,
            }}>
              {isOL && <FaExclamationTriangle size={10} />}
              {a.coach_name?.split(" ")[0]}
              <span style={{ fontSize: 11, opacity: 0.75 }}>{used.toFixed(0)}h</span>
              <button
                onClick={e => { e.stopPropagation(); removeAssign(a.id); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0, display: "flex", alignItems: "center", opacity: 0.7 }}
                title="Remove"
              >
                <FaTimes size={11} />
              </button>
            </span>
          );
        })}

        {/* Drop/tap hint */}
        {activeCoach && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 12px", borderRadius: 20, fontSize: 13, fontWeight: 700, fontFamily: FONT,
            border: `1.5px dashed ${dropColors[dropState].border || T.textMuted}`,
            color: T.textMuted, opacity: 0.8,
          }}>
            {dropState === "accept"    && (selectedCoach ? "✓ Tap to assign" : "✓ Drop to assign")}
            {dropState === "reject"    && "✕ Already assigned"}
            {dropState === "unavail"   && "✕ Coach unavailable"}
            {dropState === "overlimit" && "⚠ Will exceed limit"}
          </span>
        )}

        {!activeCoach && assigned === 0 && (
          <span style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, fontStyle: "italic" }}>
            Drag a coach here to assign, or tap a coach then tap here
          </span>
        )}
      </div>

      {errorMsg && (
        <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 700, color: getBrandColors().red, fontFamily: FONT }}>
          ✕ {errorMsg}
        </div>
      )}
    </div>
  );
}

// ─── Day Board ────────────────────────────────────────────────────────────────
function DayBoard({ dateStr, plan, cfg, availabilityMap, maxHoursMap, month, year, updateAssignments, onClose, T, S }) {
  const [batches, setBatches]   = useState([]);
  const [dragCoach, setDragCoach] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  // Tap-to-select fallback for touch devices — native HTML5 drag-and-drop
  // (dragCoach/draggingId above) never fires on iOS/Android, so tapping a
  // coach card selects them here, then tapping a batch slot assigns them.
  const [selectedCoach, setSelectedCoach] = useState(null);

  useEffect(() => {
    fetch(`${BASE}/coach-planner/batches-for-date?date=${dateStr}`)
      .then(r => r.json())
      .then(d => setBatches(d.batches || []));
  }, [dateStr]);

  const dayAssignments = (plan?.assignments || []).filter(a => a.assign_date === dateStr);
  const allAssignments = plan?.assignments || [];

  // Compute current hours from ALL plan assignments (live — recomputes when assignments change)
  const coachHoursMap = useMemo(() => computeCoachHours(allAssignments, cfg), [allAssignments, cfg]);

  // Coaches from plan (unique) + add any in maxHoursMap not in plan
  const planCoaches = [];
  const seen = new Set();
  for (const a of allAssignments) {
    if (!seen.has(a.coach_id)) {
      seen.add(a.coach_id);
      planCoaches.push({ coach_id: a.coach_id, coach_name: a.coach_name });
    }
  }

  // Is this coach available on this date?
  const dow = new Date(dateStr + "T00:00:00").getDay(); // 0=Sun
  const pythonDow = dow === 0 ? 6 : dow - 1; // convert to Mon=0...Sat=5
  const isCoachAvail = (coachId) => {
    const av = availabilityMap?.[coachId];
    if (!av) return true;
    const dayAvail = av.available_days
      ? av.available_days.split(",").map(Number).includes(pythonDow)
      : true;
    const notBlocked = !(av.blocked_dates || []).includes(dateStr);
    return dayAvail && notBlocked;
  };

  const d = new Date(dateStr + "T00:00:00");
  const dayLabel = d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  // Summary stats for this day
  const batchIds = batches.map(b => b.batch_id);
  const totalNeeded = batches.reduce((s, b) => s + Math.max(1, Math.ceil((b.enrolled_count || 1) / (parseInt(cfg?.students_per_coach) || 10))), 0);
  const totalAssigned = dayAssignments.length;
  const shortBatches = batches.filter(b => {
    const needed = Math.max(1, Math.ceil((b.enrolled_count || 1) / (parseInt(cfg?.students_per_coach) || 10)));
    const assigned = dayAssignments.filter(a => a.batch_id === b.batch_id).length;
    return assigned < needed;
  }).length;

  return (
    <div style={{ marginTop: 16, border: `2px solid ${T.yellow}`, borderRadius: 16, background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.divider}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: `rgba(247,206,11,0.06)` }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: T.text, fontFamily: FONT }}>{dayLabel}</div>
          <div style={{ display: "flex", gap: 14, marginTop: 5 }}>
            <span style={{ fontSize: 13, fontWeight: 700, fontFamily: FONT, color: totalAssigned >= totalNeeded ? getBrandColors().green : getBrandColors().orange }}>
              {totalAssigned}/{totalNeeded} slots filled
            </span>
            {shortBatches > 0 && (
              <span style={{ fontSize: 13, fontWeight: 700, fontFamily: FONT, color: getBrandColors().red }}>
                ⚠ {shortBatches} batch{shortBatches > 1 ? "es" : ""} short
              </span>
            )}
          </div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 6 }}>
          <FaTimes size={14} />
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", minHeight: 300 }}>
        {/* Coach Roster */}
        <div style={{ borderRight: `1px solid ${T.divider}`, padding: "16px 14px", overflowY: "auto", maxHeight: 480 }}>
          <div style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", color: T.textMuted, letterSpacing: "0.05em", fontFamily: FONT, marginBottom: 12 }}>
            Coach Roster — drag, or tap to select
          </div>
          {planCoaches.length === 0 && (
            <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT }}>No coaches in plan.</div>
          )}
          {planCoaches.map(coach => {
            const avail = isCoachAvail(coach.coach_id);
            const used  = coachHoursMap[coach.coach_id] || 0;
            const max   = maxHoursMap[coach.coach_id] || parseFloat(cfg?.max_coach_hours_month) || 40;
            return (
              <CoachCard
                key={coach.coach_id}
                coach={coach}
                hoursUsed={used}
                maxHours={max}
                isAvailable={avail}
                isDragging={draggingId === coach.coach_id}
                isSelected={selectedCoach?.coach_id === coach.coach_id}
                onDragStart={e => {
                  e.dataTransfer.setData("text/plain", JSON.stringify({ coach_id: coach.coach_id, coach_name: coach.coach_name }));
                  setDragCoach({ coach_id: coach.coach_id, coach_name: coach.coach_name });
                  setDraggingId(coach.coach_id);
                }}
                onDragEnd={() => { setDragCoach(null); setDraggingId(null); }}
                onClick={() => setSelectedCoach(prev =>
                  prev?.coach_id === coach.coach_id ? null : { coach_id: coach.coach_id, coach_name: coach.coach_name }
                )}
                T={T}
              />
            );
          })}
        </div>

        {/* Batch Slots */}
        <div style={{ padding: "16px 18px", overflowY: "auto", maxHeight: 480 }}>
          <div style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", color: T.textMuted, letterSpacing: "0.05em", fontFamily: FONT, marginBottom: 12 }}>
            Batch Assignments — drop or tap to assign · click ✕ to remove
          </div>
          {batches.length === 0 && (
            <div style={{ fontSize: 14, color: T.textMuted, fontFamily: FONT, padding: "24px 0", textAlign: "center" }}>
              No batches scheduled on this day.
            </div>
          )}
          {batches.map(batch => (
            <BatchSlot
              key={batch.batch_id}
              batch={batch}
              dayAssignments={dayAssignments}
              dragCoach={dragCoach}
              selectedCoach={selectedCoach}
              onCoachAssigned={() => setSelectedCoach(null)}
              month={month} year={year}
              dateStr={dateStr}
              cfg={cfg}
              coachHoursMap={coachHoursMap}
              maxHoursMap={maxHoursMap}
              availMap={availabilityMap}
              updateAssignments={updateAssignments}
              T={T} S={S}
            />
          ))}

          {/* Hint */}
          {(dragCoach || selectedCoach) && (
            <div style={{ marginTop: 8, padding: "10px 14px", borderRadius: 10, background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)", fontSize: 14, color: "#6366f1", fontFamily: FONT, display: "flex", alignItems: "center", gap: 8 }}>
              <FaInfoCircle size={11} />
              {dragCoach
                ? <>Dragging <strong>{dragCoach.coach_name}</strong> — drop on a batch above to assign</>
                : <>Selected <strong>{selectedCoach.coach_name}</strong> — tap a batch above to assign, or tap the coach again to cancel</>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Calendar View ─────────────────────────────────────────────────────────────
function CalendarView({ month, year, assignments, plan, cfg, availabilityMap, maxHoursMap, updateAssignments, T, S }) {
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => { setSelectedDate(null); }, [month, year]);

  const byDate = {};
  for (const a of assignments) {
    byDate[a.assign_date] = byDate[a.assign_date] || [];
    byDate[a.assign_date].push(a);
  }

  const firstDay  = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const startDow  = (firstDay.getDay() + 6) % 7;

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const d0 = new Date();
  const today = `${d0.getFullYear()}-${String(d0.getMonth()+1).padStart(2,'0')}-${String(d0.getDate()).padStart(2,'0')}`;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4, marginBottom: 4 }}>
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: 12, fontWeight: 800, color: T.textMuted, textTransform: "uppercase", fontFamily: FONT, padding: "6px 0" }}>{d}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const dateStr = `${year}-${String(month).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
          const items   = byDate[dateStr] || [];
          const dow     = new Date(year, month - 1, day).getDay();
          const isSun   = dow === 0;
          const isToday = dateStr === today;
          const isSel   = selectedDate === dateStr;
          const hasOL   = items.some(a => a.over_limit);
          return (
            <div
              key={dateStr}
              onClick={() => !isSun && setSelectedDate(isSel ? null : dateStr)}
              style={{
                background: isSun ? "rgba(0,0,0,0.04)" : isSel ? "rgba(247,206,11,0.12)" : T.glass,
                backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
                border: isSel ? `2px solid ${T.yellow}` : isToday ? `2px solid ${T.yellow}` : T.glassBdr,
                borderRadius: 10, padding: "8px 10px", minHeight: 82,
                cursor: isSun ? "default" : "pointer",
                opacity: isSun ? 0.35 : 1,
                boxSizing: "border-box", transition: "background 0.15s",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: isSel || isToday ? T.yellow : T.textLabel, fontFamily: FONT }}>{day}</span>
                {hasOL && <FaExclamationTriangle size={8} style={{ color: "#ef4444" }} />}
              </div>
              {items.slice(0, 2).map(a => (
                <div key={a.id} style={{
                  fontSize: 11, borderRadius: 4, padding: "2px 6px", marginBottom: 2,
                  fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: FONT,
                  background: a.over_limit ? "rgba(239,68,68,0.18)" : a.status === "approved" ? "rgba(16,185,129,0.18)" : "rgba(99,102,241,0.15)",
                  color:      a.over_limit ? getBrandColors().red               : a.status === "approved" ? getBrandColors().green              : "#6366f1",
                }}>
                  {a.over_limit ? "⚠ " : ""}{a.coach_name?.split(" ")[0]} · {a.batch_name}
                </div>
              ))}
              {items.length > 2 && <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT }}>+{items.length - 2} more</div>}
              {!isSun && items.length === 0 && (
                <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, fontStyle: "italic" }}>click to plan</div>
              )}
            </div>
          );
        })}
      </div>

      {selectedDate && (
        <DayBoard
          dateStr={selectedDate}
          plan={plan}
          cfg={cfg}
          availabilityMap={availabilityMap}
          maxHoursMap={maxHoursMap}
          month={month} year={year}
          updateAssignments={updateAssignments}
          onClose={() => setSelectedDate(null)}
          T={T} S={S}
        />
      )}
    </div>
  );
}

// ─── Coach Summary Table ───────────────────────────────────────────────────────
function CoachSummary({ coachSummary, coachHoursMap, maxHoursMap, cfg, T }) {
  const rowsWithHours = coachSummary.map(c => {
    const used = coachHoursMap?.[c.coach_id] || 0;
    const max  = maxHoursMap?.[c.coach_id] || parseFloat(cfg?.max_coach_hours_month) || 40;
    return { ...c, hours_used: used, hours_pct: max > 0 ? (used / max) * 100 : 0 };
  });
  const { sorted: sortedCoaches, toggleSort, sortArrow } = useSort(rowsWithHours, null);
  if (!coachSummary.length) return <div style={{ color: T.textMuted, fontSize: 13, fontFamily: FONT }}>No data yet</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {[
              { h: "Coach", field: "coach_name" },
              { h: "Sessions", field: "sessions" },
              { h: "Hours", field: "hours_used" },
              { h: "Balance", field: "hours_pct" },
            ].map(c => (
              <th key={c.h} onClick={() => toggleSort(c.field)} style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", color: T.textMuted, textAlign: "left", padding: "0 8px 12px 0", fontFamily: FONT, cursor: "pointer", userSelect: "none" }}>{c.h}{sortArrow(c.field)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedCoaches.map(c => {
            const used     = coachHoursMap?.[c.coach_id] || 0;
            const max      = maxHoursMap?.[c.coach_id] || parseFloat(cfg?.max_coach_hours_month) || 40;
            const overCount = c.over_limit_count || 0;
            const pct = max > 0 ? (used / max) * 100 : 0;
            const statusColor = pct >= 100 ? "#ef4444" : pct >= 80 ? "#f59e0b" : getBrandColors().green;
            const statusLabel = pct >= 100 ? "Over" : pct >= 80 ? "High" : "OK";
            return (
              <tr key={c.coach_id} style={{ borderTop: `1px solid ${T.divider}` }}>
                <td style={{ padding: "12px 8px 12px 0", fontSize: 14, fontWeight: 600, color: T.text, fontFamily: FONT }}>{c.coach_name}</td>
                <td style={{ padding: "12px 8px 12px 0", fontSize: 14, color: T.text, fontFamily: FONT }}>{c.sessions}</td>
                <td style={{ padding: "12px 8px 12px 0" }}>
                  <div style={{ minWidth: 90 }}>
                    <HoursBar used={used} max={max} compact />
                    <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 3 }}>{used.toFixed(1)}h/{max}h</div>
                  </div>
                </td>
                <td style={{ padding: "12px 0" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 6, fontFamily: FONT,
                    background: `${statusColor}22`, color: statusColor }}>
                    {overCount > 0 ? `⚠ ${overCount} OL` : statusLabel}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Edit Assignment Modal ─────────────────────────────────────────────────────
function EditModal({ assignment, coaches, onClose, onSaved, T, S }) {
  const [coachId, setCoachId] = useState(assignment.coach_id);
  const [notes, setNotes]     = useState(assignment.notes || "");
  const [saving, setSaving]   = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${BASE}/coach-planner/assignments/${assignment.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ coach_id: coachId, notes }),
      });
      onSaved(); onClose();
    } catch { setSaving(false); }
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ ...S.card, width: 420, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: FONT }}>Edit Assignment</span>
          <button style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted }} onClick={onClose}><FaTimes /></button>
        </div>
        <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT }}>{assignment.assign_date} · {assignment.batch_name}</div>
        <div>
          <label style={S.lbl}>Coach</label>
          <select style={S.inp} value={coachId} onChange={e => setCoachId(+e.target.value)}>
            {coaches.map(c => <option key={c.coach_id} value={c.coach_id}>{c.coach_name}</option>)}
          </select>
        </div>
        <div>
          <label style={S.lbl}>Notes</label>
          <textarea style={{ ...S.inp, resize: "vertical", minHeight: 60 }} value={notes} onChange={e => setNotes(e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button style={S.bBtn} onClick={onClose}>Cancel</button>
          <button style={S.yBtn} onClick={save} disabled={saving}><FaSave />{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Coach Availability Tab ────────────────────────────────────────────────────
function AvailabilityTab({ month, year, T, S }) {
  const [coaches, setCoaches]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState({});
  const [toast, setToast]         = useState(null);
  const [blockForm, setBlockForm] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${BASE}/coach-planner/availability?month=${month}&year=${year}`)
      .then(r => r.json())
      .then(d => { setCoaches(d.coaches || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const save = async (coach) => {
    setSaving(s => ({ ...s, [coach.coach_id]: true }));
    try {
      const r = await fetch(`${BASE}/coach-planner/availability?month=${month}&year=${year}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          coach_id: coach.coach_id, available_days: coach.available_days,
          blocked_dates: coach.blocked_dates || [], notes: coach.notes || "",
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Server error ${r.status}`);
      }
      // Reload from DB to confirm persisted data is what we expect
      const fresh = await fetch(`${BASE}/coach-planner/availability?month=${month}&year=${year}`).then(x => x.json());
      if (fresh.coaches) {
        setCoaches(prev => prev.map(c => {
          const updated = fresh.coaches.find(fc => fc.coach_id === c.coach_id);
          return updated ? { ...c, available_days: updated.available_days, blocked_dates: updated.blocked_dates } : c;
        }));
      }
      setToast({ ok: true, msg: `Saved — ${coach.coach_name}: ${coach.blocked_dates?.length || 0} blocked date(s)` });
    } catch (e) {
      setToast({ ok: false, msg: `Save failed: ${e.message}` });
    } finally { setSaving(s => ({ ...s, [coach.coach_id]: false })); }
  };

  const toggleDay = (coachIdx, dow) => {
    setCoaches(prev => prev.map((c, i) => {
      if (i !== coachIdx) return c;
      const daysSet = new Set((c.available_days || "0,1,2,3,4,5").split(",").map(Number));
      if (daysSet.has(dow)) daysSet.delete(dow); else daysSet.add(dow);
      return { ...c, available_days: [...daysSet].sort().join(",") };
    }));
  };

  // Use local-date formatting — toISOString() gives UTC which shifts the date in UTC+10/11
  const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  const expandRange = (from, to) => {
    const dates = []; let cur = new Date(from + "T00:00:00");
    const end = new Date(to + "T00:00:00");
    while (cur <= end) { dates.push(isoLocal(cur)); cur.setDate(cur.getDate() + 1); }
    return dates;
  };

  const addBlock = (coachIdx, coachId) => {
    const f = blockForm[coachId] || {};
    if (!f.from || !f.to || f.from > f.to) { setToast({ ok: false, msg: "Select a valid From and To date" }); return; }
    const newDates = expandRange(f.from, f.to);
    let updatedCoach = null;
    setCoaches(prev => prev.map((c, i) => {
      if (i !== coachIdx) return c;
      const existing = new Set(c.blocked_dates || []);
      newDates.forEach(d => existing.add(d));
      updatedCoach = { ...c, blocked_dates: [...existing].sort() };
      return updatedCoach;
    }));
    setBlockForm(bf => ({ ...bf, [coachId]: { from: "", to: "" } }));
    if (updatedCoach) save(updatedCoach);
  };

  const groupDates = (dates) => {
    if (!dates || !dates.length) return [];
    const sorted = [...dates].sort(); const groups = [];
    let start = sorted[0], prev = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      const cur = sorted[i];
      const prevD = new Date(prev + "T00:00:00"); prevD.setDate(prevD.getDate() + 1);
      if (isoLocal(prevD) === cur) { prev = cur; }
      else { groups.push({ from: start, to: prev }); start = cur; prev = cur; }
    }
    groups.push({ from: start, to: prev }); return groups;
  };

  const fmtDate = (s) => new Date(s + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short" });

  if (loading) return <div style={{ color: T.textMuted, fontSize: 13, fontFamily: FONT }}>Loading…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {toast && (
        <div onClick={() => setToast(null)} style={{ padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT,
          background: toast.ok ? "rgba(52,211,153,0.12)" : "rgba(248,113,113,0.12)",
          color: toast.ok ? getBrandColors().green : getBrandColors().red, border: toast.ok ? "1px solid rgba(52,211,153,0.3)" : "1px solid rgba(248,113,113,0.3)" }}>
          {toast.msg}
        </div>
      )}
      <div style={{ fontSize: 15, color: T.textMuted, fontFamily: FONT }}>
        Set each coach's available days and vacation blocks for this month. Auto-generate will respect these settings.
      </div>
      {coaches.length === 0 && (
        <div style={{ ...S.card, textAlign: "center", padding: 40, color: T.textMuted, fontSize: 13, fontFamily: FONT }}>
          No active coaches found.
        </div>
      )}
      {coaches.map((coach, ci) => {
        const daySet = new Set((coach.available_days || "0,1,2,3,4,5").split(",").map(Number));
        const activeDays = ALL_DAYS.filter(d => daySet.has(d.dow)).length;
        const blockedGroups = groupDates(coach.blocked_dates || []);
        const bf = blockForm[coach.coach_id] || { from: "", to: "" };
        return (
          <div key={coach.coach_id} style={{ ...S.card, padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(99,102,241,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FaUserTie style={{ color: "#6366f1" }} />
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: FONT }}>{coach.coach_name}</div>
                  <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
                    {activeDays} day{activeDays !== 1 ? "s" : ""}/week
                    {blockedGroups.length > 0 ? ` · ${blockedGroups.length} vacation block${blockedGroups.length !== 1 ? "s" : ""}` : ""}
                  </div>
                </div>
              </div>
              <button style={S.sBtn} onClick={() => save(coach)} disabled={saving[coach.coach_id]}>
                <FaSave size={16} />{saving[coach.coach_id] ? "Saving…" : "Save"}
              </button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", color: T.textLabel, letterSpacing: "0.05em", marginBottom: 10, fontFamily: FONT }}>Available Days</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {ALL_DAYS.map(({ dow, label }) => {
                  const on = daySet.has(dow);
                  return (
                    <button key={dow} onClick={() => toggleDay(ci, dow)} style={{
                      padding: "8px 18px", borderRadius: 20, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FONT,
                      background: on ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.10)",
                      color: on ? getBrandColors().green : getBrandColors().red,
                      border: on ? "1.5px solid rgba(16,185,129,0.35)" : "1.5px solid rgba(239,68,68,0.25)",
                      transition: "all 0.15s",
                    }}>
                      {on ? "✓" : "✕"} {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", color: T.textLabel, letterSpacing: "0.05em", marginBottom: 10, fontFamily: FONT }}>Vacation / Blocked Dates</div>
              {blockedGroups.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                  {blockedGroups.map((g, gi) => (
                    <span key={gi} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, fontFamily: FONT,
                      padding: "6px 14px 6px 12px", borderRadius: 20,
                      background: "rgba(239,68,68,0.12)", color: getBrandColors().red, border: "1.5px solid rgba(239,68,68,0.25)" }}>
                      <FaBan size={9} />
                      {g.from === g.to ? fmtDate(g.from) : `${fmtDate(g.from)} – ${fmtDate(g.to)}`}
                      <button onClick={() => {
                        const toRemove = expandRange(g.from, g.to);
                        let updatedCoach = null;
                        setCoaches(prev => prev.map((c, i) => {
                          if (i !== ci) return c;
                          updatedCoach = { ...c, blocked_dates: (c.blocked_dates || []).filter(d => !toRemove.includes(d)) };
                          return updatedCoach;
                        }));
                        if (updatedCoach) save(updatedCoach);
                      }} style={{ background: "none", border: "none", cursor: "pointer", color: getBrandColors().red, padding: "0 0 0 4px", display: "flex", alignItems: "center" }}>
                        <FaTimes size={9} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 14, color: T.textMuted, fontFamily: FONT, whiteSpace: "nowrap" }}>From</span>
                  <input type="date" value={bf.from}
                    onChange={e => setBlockForm(bf2 => ({ ...bf2, [coach.coach_id]: { ...bf2[coach.coach_id], from: e.target.value } }))}
                    style={{ ...S.inp, width: 160, padding: "8px 12px", fontSize: 14 }} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 14, color: T.textMuted, fontFamily: FONT, whiteSpace: "nowrap" }}>To</span>
                  <input type="date" value={bf.to} min={bf.from}
                    onChange={e => setBlockForm(bf2 => ({ ...bf2, [coach.coach_id]: { ...bf2[coach.coach_id], to: e.target.value } }))}
                    style={{ ...S.inp, width: 160, padding: "8px 12px", fontSize: 14 }} />
                </div>
                <button style={{ ...S.gBtn, borderColor: "rgba(239,68,68,0.3)", color: getBrandColors().red }} onClick={() => addBlock(ci, coach.coach_id)}>
                  <FaPlus size={10} /> Add Block
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main CoachPlanner ─────────────────────────────────────────────────────────
export default function CoachPlanner({ currentUser }) {
  const { isDark } = useTheme(); const T = getT(isDark); const S = makeS(T);
  const now = new Date();
  const [activeTab, setActiveTab]   = useState("plan");
  const [month, setMonth]           = useState(now.getMonth() + 1);
  const [year,  setYear]            = useState(now.getFullYear());
  const [plan,  setPlan]            = useState(null);
  const [cfg,   setCfg]             = useState(null);
  const [availabilityMap, setAvailMap] = useState({});
  const [maxHoursMap, setMaxHoursMap] = useState({});
  const [loading, setLoading]       = useState(false);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving]   = useState(false);
  const [toast, setToast]           = useState(null);
  const [editAssignment, setEditAssign] = useState(null);

  const loadPlan = useCallback(() => {
    setLoading(true);
    fetch(`${BASE}/coach-planner/plan?month=${month}&year=${year}`)
      .then(r => r.json())
      .then(d => { setPlan(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [month, year]);

  // Load config, availability and per-coach max hours
  useEffect(() => {
    fetch(`${BASE}/coach-planner/config`).then(r => r.json()).then(d => setCfg(d.config)).catch(() => {});
    fetch(`${BASE}/coach-planner/availability?month=${month}&year=${year}`)
      .then(r => r.json())
      .then(d => {
        const m = {};
        for (const c of (d.coaches || [])) {
          m[c.coach_id] = { available_days: c.available_days, blocked_dates: c.blocked_dates };
        }
        setAvailMap(m);
      }).catch(() => {});
    fetch(`${BASE}/coach-planner/coaches-hours`)
      .then(r => r.json())
      .then(d => {
        const m = {};
        for (const c of (d.coaches || [])) {
          if (c.max_hours_month) m[c.coach_id] = parseFloat(c.max_hours_month);
        }
        setMaxHoursMap(m);
      }).catch(() => {});
  }, [month, year]);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await fetch(`${BASE}/coach-planner/generate?month=${month}&year=${year}`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Generate failed");
      setToast({ ok: true, msg: `Plan generated: ${d.assignments} assignments` });
      loadPlan();
    } catch (e) { setToast({ ok: false, msg: e.message }); }
    finally { setGenerating(false); }
  };

  const approve = async () => {
    if (!window.confirm(`Approve the plan for ${MONTH_NAMES[month-1]} ${year}?`)) return;
    setApproving(true);
    try {
      const r = await fetch(`${BASE}/coach-planner/plan/approve?month=${month}&year=${year}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ approved_by: currentUser?.user_id || 1 }),
      });
      if (!r.ok) throw new Error("Approve failed");
      setToast({ ok: true, msg: "Plan approved and circulated." });
      loadPlan();
    } catch (e) { setToast({ ok: false, msg: e.message }); }
    finally { setApproving(false); }
  };

  const shiftMonth = (dir) => {
    let m = month + dir, y = year;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y);
  };

  const planStatus  = plan?.plan_status;
  const assignments = plan?.assignments || [];

  // Stable updater — patches assignments in local state without any re-fetch
  const updateAssignments = useCallback((updater) => {
    setPlan(prev => {
      if (!prev) return prev;
      return { ...prev, assignments: updater(prev.assignments || []) };
    });
  }, []);

  // Memoised so drag-drop doesn't trigger downstream recalculations unless assignments actually change
  const coachHoursMap = useMemo(() => computeCoachHours(assignments, cfg), [assignments, cfg]);

  const tabs = [
    { id: "plan",         label: "Monthly Plan",      icon: <FaCalendarAlt /> },
    { id: "availability", label: "Coach Availability", icon: <FaUserTie /> },
  ];

  return (
    <div style={{ fontFamily: FONT, maxWidth: 1300, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <PageHeaderIcon icon={<FaCalendarAlt size={20} color={getBrandColors().blueDark} />} />
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: T.text, letterSpacing: "-0.5px" }}>Coach Monthly Planner</h1>
          <div style={{ fontSize: 15, color: T.textMuted, marginTop: 5 }}>Generate, drag-and-drop, and approve monthly coaching schedules</div>
        </div>
      </div>

      {/* Month nav — always visible */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <button style={S.gBtn} onClick={() => shiftMonth(-1)}><FaChevronLeft /></button>
        <span style={{ fontSize: 18, fontWeight: 800, color: T.text, fontFamily: FONT, minWidth: 180, textAlign: "center" }}>
          {MONTH_NAMES[month-1]} {year}
        </span>
        <button style={S.gBtn} onClick={() => shiftMonth(1)}><FaChevronRight /></button>
        {planStatus && (
          <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20, textTransform: "uppercase",
            background: planStatus === "approved" ? "rgba(16,185,129,0.15)" : "rgba(251,191,36,0.15)",
            color: planStatus === "approved" ? getBrandColors().green : getBrandColors().orange, fontFamily: FONT, fontSize: 13 }}>
            {planStatus}
          </span>
        )}

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginLeft: 16 }}>
          {tabs.map(t => {
            const active = activeTab === t.id;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                padding: "9px 18px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6, fontFamily: FONT,
                border: active ? `1.5px solid rgba(255,200,0,0.45)` : T.glassBdr,
                background: active ? `linear-gradient(135deg,${T.yellow},#fff3a0)` : T.glass,
                backdropFilter: BLUR, WebkitBackdropFilter: BLUR, color: active ? getBrandColors().blueDark : T.textLabel,
                boxShadow: active ? "0 4px 14px rgba(255,200,0,0.28)" : T.glassSh,
              }}>
                {t.icon} {t.label}
              </button>
            );
          })}
        </div>

        {activeTab === "plan" && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
            <button style={S.gBtn} onClick={loadPlan}><FaSync /></button>
            <button style={S.bBtn} onClick={generate} disabled={generating}>
              <FaMagic /> {generating ? "Generating…" : "Auto-Generate"}
            </button>
            {assignments.length > 0 && planStatus !== "approved" && (
              <button style={S.yBtn} onClick={approve} disabled={approving}>
                <FaCheck /> {approving ? "Approving…" : "Approve & Circulate"}
              </button>
            )}
          </div>
        )}
      </div>

      {toast && (
        <div onClick={() => setToast(null)} style={{
          padding: "12px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: 16, fontFamily: FONT,
          background: toast.ok ? "rgba(52,211,153,0.12)" : "rgba(248,113,113,0.12)",
          color: toast.ok ? getBrandColors().green : getBrandColors().red,
          border: toast.ok ? "1px solid rgba(52,211,153,0.3)" : "1px solid rgba(248,113,113,0.3)",
        }}>
          {toast.ok ? <FaCheckCircle style={{ marginRight: 8 }} /> : <FaExclamationTriangle style={{ marginRight: 8 }} />}
          {toast.msg}
        </div>
      )}

      {/* ── PLAN TAB ── */}
      {activeTab === "plan" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {loading ? (
            <div style={{ color: T.textMuted, fontSize: 13, fontFamily: FONT }}>Loading plan…</div>
          ) : assignments.length === 0 ? (
            <div style={{ ...S.card, textAlign: "center", padding: 48 }}>
              <FaCalendarAlt size={36} style={{ color: T.textMuted, marginBottom: 12 }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: T.textMuted, fontFamily: FONT, marginBottom: 8 }}>No plan for {MONTH_NAMES[month-1]} {year}</div>
              <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, marginBottom: 20 }}>
                Auto-generate a draft, or click any calendar day to manually assign coaches.
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <button style={S.yBtn} onClick={generate} disabled={generating}>
                  <FaMagic /> {generating ? "Generating…" : "Auto-Generate Plan"}
                </button>
              </div>
            </div>
          ) : (
            <>
              <KpiBar assignments={assignments} T={T} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 260px", gap: 20 }}>
                <div style={S.card}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 5, fontFamily: FONT }}>Monthly Calendar</div>
                  <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
                    <FaGripVertical size={9} /> Click any day to open the drag-and-drop assignment board
                  </div>
                  <CalendarView
                    month={month} year={year}
                    assignments={assignments}
                    plan={plan}
                    cfg={cfg}
                    availabilityMap={availabilityMap}
                    maxHoursMap={maxHoursMap}
                    updateAssignments={updateAssignments}
                    T={T} S={S}
                  />
                </div>
                <div style={S.card}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 16, fontFamily: FONT }}>
                    <FaUserTie style={{ marginRight: 8, color: T.yellow }} />Coach Balance
                  </div>
                  <CoachSummary
                    coachSummary={plan?.coach_summary || []}
                    coachHoursMap={coachHoursMap}
                    maxHoursMap={maxHoursMap}
                    cfg={cfg}
                    T={T}
                  />
                </div>
              </div>
            </>
          )}

          {/* Allow day-board even with no plan (manual-only planning) */}
          {assignments.length === 0 && false /* placeholder — board opens from calendar */ }
        </div>
      )}

      {/* ── AVAILABILITY TAB ── */}
      {activeTab === "availability" && (
        <AvailabilityTab month={month} year={year} T={T} S={S} />
      )}

      {editAssignment && (
        <EditModal
          assignment={editAssignment}
          coaches={[...new Map(assignments.map(a => [a.coach_id, { coach_id: a.coach_id, coach_name: a.coach_name }])).values()]}
          onClose={() => setEditAssign(null)} onSaved={loadPlan} T={T} S={S}
        />
      )}
    </div>
  );
}
